import { EventController } from '../base';
import { findParent, findChild } from '../../helpers';
import { ChangeEvent, MouseEvent } from 'react';

const toTitleCase = (str: string) => (
  str.replace(/\w\S*/g, (txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()))
);

const makeTitle = (filename: string) => {
  let newFilename = filename;
  newFilename = newFilename.substr(0, newFilename.lastIndexOf('.')); // remove extension part
  newFilename = newFilename.replace(/_/g, ' '); // replace _ with space.
  return toTitleCase(newFilename);
};

interface TagInfo {
  id: number;
  name: string;
}

export default class ModuleAttributeSetFormFieldsController extends EventController {
  static targets = [
    'conditionalFieldsContainer',
    'moduleFormatSelect',
    'attrField',
    'tagsWrapper',
    'imagePreview',
    'audioPreview',
    'audioPreviewSource',
  ];

  tagsWrapperTarget: HTMLElement;
  allOptional: boolean;
  $selectizedTagsInput: JQuery<HTMLElement>;
  attrFieldTargets: HTMLElement[];
  imagePreviewTargets: HTMLElement[];
  audioPreviewTargets: HTMLElement[];
  audioPreviewSourceTargets: HTMLElement[];
  moduleFormatSelectTarget: HTMLSelectElement;
  conditionalFieldsContainerTarget: HTMLElement;
  lastDefaultsCache: any; // TODO: use a real type here

  connect() {
    this.loadModuleFormat();
    this.lastDefaultsCache = {};
    const tags: TagInfo[] = JSON.parse(this.data.get('tags'));
    this.allOptional = JSON.parse(this.data.get('allOptional'));
    this.$selectizedTagsInput = ($(this.tagsWrapperTarget).find('input') as any).selectize({
      persist: false,
      maxItems: null,
      name: 'tags',
      displayKey: 'text',
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      options: tags.map(tag => ({ value: tag.id, text: tag.name })),
    });
    (this.$selectizedTagsInput[0] as any).selectize.$control_input.on('keypress', (evt: KeyboardEvent) => {
      // keep this from submitting the form on enter
      if (evt.charCode === 13) evt.preventDefault();
    });
    $(this.tagsWrapperTarget).show();
    this.addEventListener('SOURCES_CHANGED', (_evt: any, supersourceId: number, subsourceId: number) => {
      this.cascadeDefaults(supersourceId, subsourceId);
    });
    this.setFileClasses();
  }

  onFileChange(evt: ChangeEvent<HTMLInputElement>) {
    const file = evt.currentTarget.files[0];
    if (file.size > 40 * 1024 * 1024) {
      alert('Max allowed file size is 40 MB');
      return;
    }

    const { attrField } = evt.currentTarget.dataset;
    const $wrapper = this.findAttrWrapper(attrField);

    $wrapper.find('.js_filename').val(file.name);
    $wrapper.find('.js_file-title').val(makeTitle(file.name));
    // enable the disabled title field.
    $wrapper.find('.js_file-title').prop('disabled', false);
    this.setFileClasses();
  }

  onImageChange(evt: ChangeEvent<HTMLInputElement>) {
    const file = evt.currentTarget.files[0];
    if (file.size > 40 * 1024 * 1024) {
      alert('Max allowed file size is 40 MB');
      return;
    }

    const $attrField = findParent(evt.currentTarget, this.attrFieldTargets);
    const $imagePreview = findChild($attrField, this.imagePreviewTargets);

    const reader = new FileReader();
    reader.onload = e => $imagePreview.attr('src', (e.target as any).result);
    reader.readAsDataURL(file);
  }

  onAudioChange(evt: ChangeEvent<HTMLInputElement>) {
    const file = evt.currentTarget.files[0];
    if (file.size > 40 * 1024 * 1024) {
      alert('Max allowed file size is 40 MB');
      return;
    }

    const $attrField = findParent(evt.currentTarget, this.attrFieldTargets);
    const $audioPreview = findChild($attrField, this.audioPreviewTargets);
    const $audioPreviewSource = findChild($attrField, this.audioPreviewSourceTargets);

    const reader = new FileReader();
    reader.onload = e => {
      $audioPreviewSource.attr('src', (e.target as any).result);
      ($audioPreview as any)[0].load();
      this.toggleAudioPreview();
    };
    reader.readAsDataURL(file);
  }

  onDeleteFile(evt: MouseEvent<HTMLElement>) {
    evt.preventDefault();
    const $attrField = findParent(evt.currentTarget, this.attrFieldTargets);
    $attrField.find('.js_file-override-id').val(null);
    $attrField.find('.js_filename').val(null);
    $attrField.find('.js_file-title').val(null);
    $attrField.find('.js_file').val(null);
    this.setFileClasses();
  }

  setFileClasses() {
    ['content_file', 'teacher_file'].forEach((fileAttr) => {
      const $wrapper = this.findAttrWrapper(fileAttr);
      const hasFile = !!$wrapper.find('.js_filename').val() || !!$wrapper.find('.js_file-title').val();
      $wrapper.toggleClass('has-file', hasFile);
    });
  }

  loadModuleFormat() {
    const moduleFormatId = $(this.moduleFormatSelectTarget).val();
    $(this.conditionalFieldsContainerTarget).toggleClass('hidden', moduleFormatId === '__None');
    if (moduleFormatId === '__None') return;
    $.get(`/admin/api/lesson/module-format/${moduleFormatId}`, (moduleAttributes) => {
      this.changeAttributeDisplay(moduleAttributes, 'instructions_c_en');
      this.changeAttributeDisplay(moduleAttributes, 'instructions_c_zh');
      this.changeAttributeDisplay(moduleAttributes, 'instructions_t_zh');
      this.changeAttributeDisplay(moduleAttributes, 'content_link');
      this.changeAttributeDisplay(moduleAttributes, 'content_text');
      this.changeAttributeDisplay(moduleAttributes, 'content_image');
      this.changeAttributeDisplay(moduleAttributes, 'content_audio');
      this.changeAttributeDisplay(moduleAttributes, 'reference_link');
      this.changeAttributeDisplay(moduleAttributes, 'admin_notes');
      this.changeAttributeDisplay(moduleAttributes, 'content_file');
      this.changeAttributeDisplay(moduleAttributes, 'teacher_file');
      this.toggleAudioPreview();
    });
  }

  findAttrWrapper(attribute: string) {
    return $(this.attrFieldTargets.find(elm => elm.dataset.attrField === attribute));
  }

  changeAttributeDisplay(moduleAttributes: any, attribute: string) {
    const $wrapper = this.findAttrWrapper(attribute);
    $wrapper.toggleClass('required', moduleAttributes[attribute] === 'REQUIRED');
    $wrapper.toggleClass('hidden', moduleAttributes[attribute] === 'HIDDEN');
    $wrapper.toggleClass('hidden', moduleAttributes[attribute] === 'HIDDEN');
    if (moduleAttributes[attribute] === 'REQUIRED' && !this.allOptional) {
      $wrapper
        .find('input')
        // exclude file fields, since they break when set to required since they're not visible
        .filter((i, elm) => $(elm).attr('type') !== 'file')
        .attr('required', 'required');
    } else {
      $wrapper.find('input').removeAttr('required');
    }
  }

  async cascadeDefaults(supersourceId: number, subsourceId: number) {
    const defaults = [];
    if (supersourceId) {
      const attributes = await $.get(`/admin/api/supersource/${supersourceId}/attributes`);
      if (attributes) defaults.push(attributes);
    }
    if (subsourceId) {
      const attributes = await $.get(`/admin/api/subsource/${subsourceId}/attributes`);
      if (attributes) defaults.push(attributes);
    }
    if (defaults.length === 0) return;

    this.setupDefault('content_category', defaults);
    this.setupDefault('module_format', defaults);
    this.loadModuleFormat();

    this.setupDefaultTags(defaults);
    this.setupDefaultLevels(defaults);

    this.setupDefault('title_en', defaults);
    this.setupDefault('title_zh', defaults);
    this.setupDefault('admin_notes', defaults);
    this.setupDefault('instructions_c_en', defaults);
    this.setupDefault('instructions_c_zh', defaults);
    this.setupDefault('instructions_t_zh', defaults);
    this.setupDefault('content_link', defaults);
    this.setupDefault('content_image', defaults);
    this.setupDefault('content_audio', defaults);
    this.setupDefault('content_text', defaults);
    this.setupDefault('reference_link', defaults);

    this.setupDefaultFile('content_file', defaults);
    this.setupDefaultFile('teacher_file', defaults);
  }

  // TODO: improve these types
  hasDefaultChanged(attrField: string, defaultCascades: any) {
    // Need to track if fields have changed since the last time we tried to cascade
    // otherwise we'll keep overwriting fields with their default values even after
    // users have explicitly changed those defaults
    const defaultVal = this.getCascadeVal(attrField, defaultCascades);
    const lastVal = this.lastDefaultsCache[attrField];
    const hasChanged = JSON.stringify(defaultVal) !== JSON.stringify(lastVal);
    this.lastDefaultsCache[attrField] = defaultVal;
    return hasChanged;
  }

  getCascadeVal(attrField: string, defaultCascades: any): any {
    let defaultVal = null;
    defaultCascades.forEach((moduleAttrSetData: any) => {
      if (!moduleAttrSetData[attrField] || moduleAttrSetData[attrField].length === 0) return;
      defaultVal = moduleAttrSetData[attrField];
    });
    return defaultVal;
  }

  setupDefaultTags(defaultCascades: any) {
    const defaultTagIds = this.getCascadeVal('tag_ids', defaultCascades);
    if (!defaultTagIds || !this.hasDefaultChanged('tag_ids', defaultCascades)) return;

    (this.$selectizedTagsInput[0] as any).selectize.clear();
    defaultTagIds.forEach((tagId: number) => {
      (this.$selectizedTagsInput[0] as any).selectize.addItem(tagId);
    });
  }

  setupDefaultLevels(defaultCascades: any) {
    const defaultLevelIds = this.getCascadeVal('levels', defaultCascades);
    if (!defaultLevelIds || !this.hasDefaultChanged('levels', defaultCascades)) return;
    const $wrapper = this.findAttrWrapper('levels');
    $wrapper.find('input').each((i, elm) => {
      $(elm).attr('checked', defaultLevelIds.indexOf(parseInt($(elm).val() as string, 10)) >= 0 as any);
    });
  }

  setupDefaultFile(attrField: string, defaultCascades: any) {
    const defaultFileData = this.getCascadeVal(attrField, defaultCascades);
    if (!defaultFileData || !this.hasDefaultChanged(attrField, defaultCascades)) return;

    const $wrapper = this.findAttrWrapper(attrField);
    $wrapper.find('.js_file-override-id').val(defaultFileData.id);
    $wrapper.find('.js_filename').val(defaultFileData.filename);
    $wrapper.find('.js_file-title').val(defaultFileData.title);
    this.setFileClasses();
  }

  setupDefault(attrField: string, defaultCascades: any) {
    const defaultVal = this.getCascadeVal(attrField, defaultCascades);
    if (!defaultVal || !this.hasDefaultChanged(attrField, defaultCascades)) return;

    const $wrapper = this.findAttrWrapper(attrField);
    $wrapper.find('input, select').val(defaultVal);
  }

  toggleAudioPreview() {
    this.audioPreviewTargets.forEach((audioPreview) => {
      const $audioPreviewSource = findChild(audioPreview, this.audioPreviewSourceTargets);
      $(audioPreview).toggleClass('hidden', !$audioPreviewSource.attr('src'));
    });
  }
}
